import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    ArrowLeft, Lock, Loader2, Users, Search,
    Pencil, Trash2, X, Check, RefreshCw
} from 'lucide-react'
import './AdminPanel.css'

function AdminPanel() {
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const [adminPassword, setAdminPassword] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState('')
    const [leads, setLeads] = useState([])
    const [searchTerm, setSearchTerm] = useState('')
    const [editingId, setEditingId] = useState(null)
    const [editForm, setEditForm] = useState({})
    const [adminToken, setAdminToken] = useState('')
    const navigate = useNavigate()

    const handleAdminLogin = async (e) => {
        e.preventDefault()
        setIsLoading(true)
        setError('')

        try {
            const response = await fetch('/api/api/auth/admin-login.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ password: adminPassword })
            })

            const data = await response.json()

            if (response.ok && data.token) {
                setAdminToken(data.token)
                localStorage.setItem('admin_token', data.token)
                setIsAuthenticated(true)
                fetchLeads(data.token)
            } else {
                setError(data.error || 'Contraseña incorrecta')
            }
        } catch (err) {
            setError('Error de conexión. Verifica tu conexión a internet.')
        }
        setIsLoading(false)
    }

    const fetchLeads = async (token = adminToken) => {
        if (!token) {
            token = localStorage.getItem('admin_token')
        }

        try {
            const response = await fetch('/api/api/leads/list.php', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            })

            if (response.ok) {
                const data = await response.json()
                if (data.leads) {
                    setLeads(data.leads)
                } else if (Array.isArray(data)) {
                    setLeads(data)
                }
            } else if (response.status === 401) {
                setIsAuthenticated(false)
                setError('Sesión expirada. Por favor, ingresa de nuevo.')
            }
        } catch (err) {
            console.error('Error fetching leads:', err)
            setError('Error al cargar leads')
        }
    }

    const handleEdit = (lead) => {
        setEditingId(lead.id)
        setEditForm({ ...lead })
    }

    const handleSaveEdit = async () => {
        const token = adminToken || localStorage.getItem('admin_token')
        try {
            await fetch('/api/api/leads/update.php', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(editForm)
            })
        } catch (err) {
            console.error('Error updating lead:', err)
        }

        setLeads(leads.map(l => l.id === editingId ? editForm : l))
        setEditingId(null)
        setEditForm({})
    }

    const handleDelete = async (id) => {
        if (!confirm('¿Está seguro de eliminar este lead?')) return

        const token = adminToken || localStorage.getItem('admin_token')
        try {
            await fetch(`/api/api/leads/delete.php?id=${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            })
        } catch (err) {
            console.error('Error deleting lead:', err)
        }

        setLeads(leads.filter(l => l.id !== id))
    }

    const handleStatusChange = async (id, newStatus) => {
        const updatedLeads = leads.map(l =>
            l.id === id ? { ...l, estado: newStatus } : l
        )
        setLeads(updatedLeads)
    }

    const filteredLeads = leads.filter(lead =>
        lead.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.empresa.toLowerCase().includes(searchTerm.toLowerCase())
    )

    if (!isAuthenticated) {
        return (
            <div className="admin-page">
                <div className="admin-login-container">
                    <button className="back-button" onClick={() => window.location.href = '/index.html'}>
                        <ArrowLeft size={20} />
                        <span>Volver</span>
                    </button>

                    <div className="admin-login-card glass-card">
                        <div className="admin-login-header">
                            <div className="admin-icon">
                                <Lock size={32} />
                            </div>
                            <h1>Panel de Administración</h1>
                            <p>Ingrese la contraseña de administrador</p>
                        </div>

                        <form onSubmit={handleAdminLogin}>
                            <div className="form-group">
                                <input
                                    type="password"
                                    className="form-input"
                                    placeholder="Contraseña de administrador"
                                    value={adminPassword}
                                    onChange={(e) => setAdminPassword(e.target.value)}
                                    required
                                />
                            </div>

                            {error && <div className="form-error">{error}</div>}

                            <button
                                type="submit"
                                className="btn btn-primary btn-lg"
                                style={{ width: '100%' }}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 size={20} className="animate-spin" />
                                        Verificando...
                                    </>
                                ) : (
                                    'Acceder'
                                )}
                            </button>
                        </form>

                        <p className="demo-hint"></p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="admin-page">
            <div className="admin-container">
                <header className="admin-header">
                    <div className="admin-header-left">
                        <button className="back-button" onClick={() => window.location.href = '/index.html'}>
                            <ArrowLeft size={20} />
                            <span>Volver</span>
                        </button>
                        <h1>
                            <Users size={24} />
                            Gestión de Leads
                        </h1>
                    </div>
                    <div className="admin-header-right">
                        <div className="search-box">
                            <Search size={18} />
                            <input
                                type="text"
                                placeholder="Buscar leads..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button className="btn btn-secondary" onClick={fetchLeads}>
                            <RefreshCw size={18} />
                        </button>
                    </div>
                </header>

                <div className="leads-table-container glass-card">
                    <table className="leads-table">
                        <thead>
                            <tr>
                                <th>Nombre</th>
                                <th>Cargo</th>
                                <th>Email</th>
                                <th>Empresa</th>
                                <th>Estado</th>
                                <th>Fecha</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredLeads.map(lead => (
                                <tr key={lead.id}>
                                    {editingId === lead.id ? (
                                        <>
                                            <td>
                                                <input
                                                    type="text"
                                                    value={editForm.nombre}
                                                    onChange={(e) => setEditForm({ ...editForm, nombre: e.target.value })}
                                                    className="edit-input"
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    value={editForm.cargo}
                                                    onChange={(e) => setEditForm({ ...editForm, cargo: e.target.value })}
                                                    className="edit-input"
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="email"
                                                    value={editForm.email}
                                                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                                    className="edit-input"
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    value={editForm.empresa}
                                                    onChange={(e) => setEditForm({ ...editForm, empresa: e.target.value })}
                                                    className="edit-input"
                                                />
                                            </td>
                                            <td>
                                                <select
                                                    value={editForm.estado}
                                                    onChange={(e) => setEditForm({ ...editForm, estado: e.target.value })}
                                                    className="edit-select"
                                                >
                                                    <option value="pendiente">Pendiente</option>
                                                    <option value="activo">Activo</option>
                                                    <option value="inactivo">Inactivo</option>
                                                </select>
                                            </td>
                                            <td>{lead.fecha_registro}</td>
                                            <td>
                                                <div className="action-buttons">
                                                    <button className="action-btn save" onClick={handleSaveEdit}>
                                                        <Check size={16} />
                                                    </button>
                                                    <button className="action-btn cancel" onClick={() => setEditingId(null)}>
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td>{lead.nombre}</td>
                                            <td>{lead.cargo}</td>
                                            <td>{lead.email}</td>
                                            <td>{lead.empresa}</td>
                                            <td>
                                                <span className={`status-badge status-${lead.estado}`}>
                                                    {lead.estado}
                                                </span>
                                            </td>
                                            <td>{lead.fecha_registro}</td>
                                            <td>
                                                <div className="action-buttons">
                                                    <button className="action-btn edit" onClick={() => handleEdit(lead)}>
                                                        <Pencil size={16} />
                                                    </button>
                                                    <button className="action-btn delete" onClick={() => handleDelete(lead.id)}>
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {filteredLeads.length === 0 && (
                        <div className="no-leads">
                            <Users size={48} />
                            <p>No se encontraron leads</p>
                        </div>
                    )}
                </div>

                <div className="admin-stats">
                    <div className="stat-card glass-card">
                        <span className="stat-value">{leads.length}</span>
                        <span className="stat-label">Total Leads</span>
                    </div>
                    <div className="stat-card glass-card">
                        <span className="stat-value">{leads.filter(l => l.estado === 'activo').length}</span>
                        <span className="stat-label">Activos</span>
                    </div>
                    <div className="stat-card glass-card">
                        <span className="stat-value">{leads.filter(l => l.estado === 'pendiente').length}</span>
                        <span className="stat-label">Pendientes</span>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AdminPanel
